'use client';

import { Children, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ServerDrivenCarouselProps {
    children: ReactNode;
    className?: string;
    intervalMs?: number;
}

export default function ServerDrivenCarousel({
    children,
    className = '',
    intervalMs = 5000,
}: ServerDrivenCarouselProps) {
    const slides = useMemo(() => Children.toArray(children), [children]);
    const trackRef = useRef<HTMLDivElement | null>(null);
    const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [isPaused, setIsPaused] = useState(false);
    const [isDesktop, setIsDesktop] = useState(false);
    const [maxSlideHeight, setMaxSlideHeight] = useState(0);

    const setSlideRef = useCallback((index: number, node: HTMLDivElement | null) => {
        slideRefs.current[index] = node;
    }, []);

    const moveBySlide = useCallback((direction: 1 | -1) => {
        const track = trackRef.current;
        if (!track) {
            return;
        }

        const firstSlide = slideRefs.current[0];
        const slideWidth = firstSlide?.getBoundingClientRect().width ?? track.clientWidth;
        const maxScrollLeft = track.scrollWidth - track.clientWidth;
        const nextScrollLeft = track.scrollLeft + direction * slideWidth;

        if (nextScrollLeft > maxScrollLeft + 1) {
            track.scrollTo({ left: 0, behavior: 'smooth' });
            return;
        }

        if (nextScrollLeft < 0) {
            track.scrollTo({ left: maxScrollLeft, behavior: 'smooth' });
            return;
        }

        track.scrollTo({ left: nextScrollLeft, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(min-width: 768px)');

        const syncMode = () => {
            setIsDesktop(mediaQuery.matches);
        };

        syncMode();
        mediaQuery.addEventListener('change', syncMode);

        return () => {
            mediaQuery.removeEventListener('change', syncMode);
        };
    }, []);

    useEffect(() => {
        if (!isDesktop || slides.length <= 1 || isPaused) {
            return;
        }

        const interval = window.setInterval(() => {
            moveBySlide(1);
        }, intervalMs);

        return () => {
            window.clearInterval(interval);
        };
    }, [intervalMs, isDesktop, isPaused, moveBySlide, slides.length]);

    useEffect(() => {
        if (!isDesktop) {
            return;
        }

        const nodes = slideRefs.current.filter(Boolean) as HTMLDivElement[];
        if (nodes.length === 0) {
            return;
        }

        const measure = () => {
            const tallest = nodes.reduce((max, node) => {
                return Math.max(max, node.getBoundingClientRect().height);
            }, 0);
            setMaxSlideHeight(tallest);
        };

        measure();

        const observer = new ResizeObserver(measure);
        nodes.forEach((node) => observer.observe(node));
        window.addEventListener('resize', measure);

        return () => {
            window.removeEventListener('resize', measure);
            observer.disconnect();
        };
    }, [isDesktop, slides]);

    useEffect(() => {
        const track = trackRef.current;
        if (!track) {
            return;
        }

        track.scrollTo({ left: 0, behavior: 'auto' });
    }, [isDesktop]);

    if (slides.length === 0) {
        return null;
    }

    const equalizedHeightStyle = isDesktop && maxSlideHeight > 0 ? { minHeight: `${maxSlideHeight}px` } : undefined;

    return (
        <div className={`mt-12 ${className}`}>
            <div className="flex flex-col gap-8 md:hidden">
                {slides.map((slide, index) => (
                    <div key={index} className="w-full">
                        {slide}
                    </div>
                ))}
            </div>

            <div
                className="relative hidden md:block"
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
            >
                <div className="flex items-center justify-center gap-2">

                    <button
                        type="button"
                        onClick={() => moveBySlide(-1)}
                        className="rounded-full border border-border border-primary bg-primary/30 p-2 text-text shadow-sm transition hover:bg-primary/70 md:-left-6"
                        aria-label="Vorherige Referenz"
                    >
                        <ChevronLeft size={20} />
                    </button>
                    <button
                        type="button"
                        onClick={() => moveBySlide(1)}
                        className="bg-primary/30 rounded-full border border-border border-primary p-2 text-text shadow-sm transition hover:bg-primary/70 md:-right-6"
                        aria-label="Nächste Referenz"
                    >
                        <ChevronRight size={20} />
                    </button>
                </div>

                <div
                    ref={trackRef}
                    className="overflow-x-auto scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                >
                    <div className="flex items-center">
                        {slides.map((slide, index) => (
                            <div
                                key={index}
                                className="shrink-0 basis-full snap-start"
                            >
                                <div ref={(node) => setSlideRef(index, node)} style={equalizedHeightStyle} className='p-10 flex lg:items-center'>
                                    {slide}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
